import React, { useEffect, useState } from 'react';
import { Spinner } from 'react-bootstrap';
import { Modal } from "react-bootstrap";
import { useHistory } from 'react-router-dom';
import {
    FaEdit, FaTrash, FaSearch, FaFilter, FaMapMarkerAlt,
    FaToggleOn, FaStar, FaExclamationCircle, FaExclamationTriangle,
    FaRedo, FaShoppingBag, FaCircle, FaBoxOpen, FaKey, FaClock,
} from 'react-icons/fa';
import { Switch } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useDispatch, useSelector } from 'react-redux';
import retailerWholesalerPlaceholderImage from '../assets/img/restaurantimage_unavailable.png';
import { getUserFromLocalStorage } from 'utilities/user.js';
import { fetchRetailersWholesalers, updateRetailerWholesalerProfile, deleteRetailerWholesaler } from '../actions/retailerWholesalerProfileActions.js';
import {
    apiToggleRetailerAIShoppable,
    apiGetVendorOpenStatus,
    apiUpdateVendorOpenHours,
} from '../api/index';
import OpenHoursManager from '../components/OpenHours/OpenHoursManager';
import { toast } from 'react-toastify';
import { notify } from '../utilities/notify';
import EditRetailerWholesalerForm from './EditRetailerWholesalerForm.js';
import { usePermissions } from '../utilities/usePermissions.js';
import SendVendorCredentialsAction from '../components/VendorCredentials/SendVendorCredentialsAction';
import SignInAsVendorButton from '../components/SignInAsVendorButton';
import AppSelect from "../components/ui/AppSelect";

const useStyles = makeStyles(theme => ({
    switchBase: {
        color: theme.palette.secondary.main,
        '&$checked': { color: theme.palette.secondary.main },
        '&$checked + $track': { backgroundColor: theme.palette.secondary.main },
    },
    promotedSwitchBase: {
        color: theme.palette.primary.main,
        '&$checked': { color: theme.palette.primary.main },
        '&$checked + $track': { backgroundColor: theme.palette.primary.main },
    },
    checked: {},
    track: {},
}));

const RetailersWholesalers = () => {
    const classes = useStyles();
    const dispatch = useDispatch();
    const { can, loaded: permissionsLoaded } = usePermissions();
    const { loading, retailersWholesalers, error } = useSelector((state) => state.retailerWholesalerProfile);
    const [editingRetailerWholesaler, setEditingRetailerWholesaler] = useState(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [retailerWholesalerToDelete, setRetailerWholesalerToDelete] = useState(null);
    // Business hours. The whole stack for this already existed — openHours on
    // RetailerWholesalerModel, GET /rw/:id/open-status, PATCH /rw/:vendorId/open-hours
    // and the shared OpenHoursManager — but the only way in was the vendor's own
    // profile page, so ops could not set hours on a vendor's behalf the way they
    // can for restaurants. This is that missing entry point.
    const [showHoursModal, setShowHoursModal] = useState(false);
    const [vendorForHours, setVendorForHours] = useState(null);
    const [vendorHours, setVendorHours] = useState(null);
    const [isLoadingHours, setIsLoadingHours] = useState(false);
    const [isSavingHours, setIsSavingHours] = useState(false);

    const user = getUserFromLocalStorage();
    const adminId = user?.result?._id;
    const isSuperAdmin = user?.result?.userType === 'LUSUKU STAFF' && user?.result?.user_role === 'SUPERUSER';
    const canManageRetailWholesale =
        isSuperAdmin || (permissionsLoaded && can('retail_wholesale:manage'));
    const canUpdateRetailWholesale =
        canManageRetailWholesale || (permissionsLoaded && can('retail_wholesale:update'));
    const canDeleteRetailWholesale =
        canManageRetailWholesale || (permissionsLoaded && can('retail_wholesale:delete'));
    const canViewDirectory =
        user?.result?.userType === 'LUSUKU STAFF' &&
        (isSuperAdmin || !permissionsLoaded || can('retail_wholesale:read'));
    const history = useHistory();

    /* ── Live product listing counts + catalog QC ─────────────────────────
       The list response nests each vendor's sections with their products,
       so the counts reflect the DB at fetch time. `live` = enabled listings
       customers can actually see; `total` includes hidden ones. */
    const productStats = (rw) => {
        const products = (rw.sections || []).flatMap((s) => s?.products || []);
        return {
            total: products.length,
            live: products.filter((p) => p?.enabled).length,
        };
    };
    // Catalog QC modal — lists a vendor's products so a super admin can
    // jump in and polish an individual listing (opens the product editor).
    const [qcVendor, setQcVendor] = useState(null);

    const handleQcEditProduct = (product, sectionTitle) => {
        if (!canUpdateRetailWholesale) return;
        history.push({
            pathname: `/admin/edit-catalog-product/${product._id}`,
            state: {
                productDetails: product,
                qcAdmin: true,
                returnTo: '/admin/rw-vendors',
                sectionTitle,
            },
        });
    };
    const [aiToggleLoading, setAiToggleLoading] = useState({});
    const [showAIModal, setShowAIModal] = useState(false);
    const [aiModalTarget, setAiModalTarget] = useState(null); // { id, name, currentValue }

    const handleToggleAIShoppable = (vendorId, vendorName, currentValue) => {
        if (!canManageRetailWholesale) return;
        setAiModalTarget({ id: vendorId, name: vendorName, currentValue });
        setShowAIModal(true);
    };

    const confirmToggleAI = async () => {
        if (!aiModalTarget || !canManageRetailWholesale) return;
        const { id, currentValue } = aiModalTarget;
        const next = !currentValue;
        setShowAIModal(false);
        setAiToggleLoading((prev) => ({ ...prev, [id]: true }));
        try {
            await apiToggleRetailerAIShoppable(id, next);
            toast.success(`AI Commerce ${next ? 'enabled' : 'disabled'} successfully.`);
            dispatch(fetchRetailersWholesalers(adminId, canViewDirectory));
        } catch (error) {
            toast.error('Failed to update AI-shoppable setting');
        } finally {
            setAiToggleLoading((prev) => ({ ...prev, [id]: false }));
            setAiModalTarget(null);
        }
    };

    useEffect(() => {
        dispatch(fetchRetailersWholesalers(adminId, canViewDirectory));
    }, [dispatch, adminId, canViewDirectory]);

    const handleRetry = () => dispatch(fetchRetailersWholesalers(adminId, canViewDirectory));

    const handleDeleteRetailerWholesaler = (id) => {
        if (canDeleteRetailWholesale) { setRetailerWholesalerToDelete(id); setShowDeleteModal(true); }
    };

    const confirmDeleteRetailerWholesaler = () => {
        if (retailerWholesalerToDelete && canDeleteRetailWholesale) {
            dispatch(deleteRetailerWholesaler(retailerWholesalerToDelete));
            setShowDeleteModal(false);
        }
    };

    const handleEditClick = (rw) => {
        if (canUpdateRetailWholesale) setEditingRetailerWholesaler(rw);
    };

    const handleSave = async (updated) => {
        if (!canUpdateRetailWholesale) return;
        try {
            await dispatch(updateRetailerWholesalerProfile(updated.owner, updated));
            notify.success('Shop updated');
            setEditingRetailerWholesaler(null);
        } catch (err) {
            // The form stays open with the changes so they can be corrected.
            notify.fromError(err, 'Could not save the shop');
        }
    };

    const handleManageHours = async (rw) => {
        if (!canUpdateRetailWholesale) return;
        setVendorForHours(rw);
        setShowHoursModal(true);
        setIsLoadingHours(true);
        try {
            // Always the vendor-type-aware helper. The restaurant-flavoured
            // apiUpdateOpenHours/apiGetOpenStatus are hardcoded to /restaurants —
            // using them here would PATCH the wrong collection entirely.
            const response = await apiGetVendorOpenStatus('retailer', rw._id);
            setVendorHours(response.data.openHours || null);
        } catch (error) {
            console.error('Error fetching vendor hours:', error);
            toast.error('Failed to load business hours');
            setVendorHours(null);
        } finally {
            setIsLoadingHours(false);
        }
    };

    const handleSaveVendorHours = async (hoursData, timezone) => {
        if (!vendorForHours || !canUpdateRetailWholesale) return;
        setIsSavingHours(true);
        try {
            await apiUpdateVendorOpenHours('retailer', vendorForHours._id, {
                openHours: hoursData,
                ...(timezone ? { timezone } : {}),
            });
            toast.success(`Business hours updated for ${vendorForHours.name}!`);
            setShowHoursModal(false);
            dispatch(fetchRetailersWholesalers(adminId, canViewDirectory));
        } catch (error) {
            console.error('Error saving vendor hours:', error);
            toast.error(
                error?.response?.data?.message || 'Failed to update business hours'
            );
        } finally {
            setIsSavingHours(false);
        }
    };

    const handleCancel = () => setEditingRetailerWholesaler(null);
    const toggleDeleteModal = () => setShowDeleteModal(!showDeleteModal);

    // ── KPI counts ────────────────────────────────────────────────────────
    const total    = retailersWholesalers?.length || 0;
    const active   = retailersWholesalers?.filter(r => r.isActive).length || 0;
    const promoted = retailersWholesalers?.filter(r => r.promoted).length || 0;
    const inactive = total - active;

    // ── Filter state ──────────────────────────────────────────────────────
    const [searchQuery,    setSearchQuery]    = useState('');
    const [statusFilter,   setStatusFilter]   = useState('all');
    const [promotedFilter, setPromotedFilter] = useState('all');

    const filtered = (retailersWholesalers || []).filter(r => {
        const matchesSearch = !searchQuery ||
            r.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            r.address?.toLowerCase().includes(searchQuery.toLowerCase());
        const matchesStatus =
            statusFilter === 'all' ||
            (statusFilter === 'active'   && r.isActive) ||
            (statusFilter === 'inactive' && !r.isActive);
        const matchesPromoted =
            promotedFilter === 'all' ||
            (promotedFilter === 'promoted'     && r.promoted) ||
            (promotedFilter === 'not-promoted' && !r.promoted);
        return matchesSearch && matchesStatus && matchesPromoted;
    });

    const hasActiveFilters = searchQuery || statusFilter !== 'all' || promotedFilter !== 'all';
    const clearFilters = () => { setSearchQuery(''); setStatusFilter('all'); setPromotedFilter('all'); };

    return (
        <>
            <style>{`
                .rw-page { background: #f4f6f9; min-height: 100vh; padding: 24px 20px; font-family: "Segoe UI", system-ui, sans-serif; }

                /* KPI */
                .rw-kpi-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 16px; margin-bottom: 24px; }
                .rw-kpi-card { background: #fff; border-radius: 10px; padding: 18px 20px; box-shadow: 0 1px 6px rgba(0,0,0,0.07); border-top: 4px solid transparent; position: relative; overflow: hidden; }
                .rw-kpi-label { font-size: 0.7rem; font-weight: 700; letter-spacing: 0.08em; text-transform: uppercase; color: #8a9ab0; margin-bottom: 6px; }
                .rw-kpi-value { font-size: 2rem; font-weight: 800; line-height: 1; margin-bottom: 4px; }
                .rw-kpi-sub   { font-size: 0.75rem; color: #8a9ab0; }
                .rw-kpi-icon  { position: absolute; right: 16px; top: 50%; transform: translateY(-50%); opacity: 0.09; font-size: 48px; pointer-events: none; }

                /* Panel */
                .rw-panel { background: #fff; border-radius: 10px; box-shadow: 0 1px 6px rgba(0,0,0,0.07); overflow: hidden; }

                /* Dark header */
                .rw-header { background: linear-gradient(135deg, #1a2a3a 0%, #243447 100%); padding: 16px 24px; display: flex; align-items: center; justify-content: space-between; gap: 12px; flex-wrap: wrap; }
                .rw-header-title { color: #fff; font-size: 1.1rem; font-weight: 800; margin: 0; display: flex; align-items: center; gap: 8px; }
                .rw-header-sub { color: #8aaccc; font-size: 0.78rem; margin: 2px 0 0; }

                /* Filter bar */
                .rw-filter-bar { display: flex; align-items: center; gap: 10px; padding: 12px 20px; background: #f8fafc; border-bottom: 1px solid #e9ecef; flex-wrap: wrap; }
                .rw-filter-search { position: relative; flex: 1; min-width: 180px; max-width: 280px; }
                .rw-filter-search-icon { position: absolute; left: 10px; top: 50%; transform: translateY(-50%); color: #9aacbf; font-size: 13px; pointer-events: none; }
                .rw-filter-search input { width: 100%; padding: 7px 12px 7px 32px; border: 1.5px solid #e4e8ef; border-radius: 8px; font-size: 0.82rem; outline: none; background: #fff; transition: border-color 0.2s; }
                .rw-filter-search input:focus { border-color: #5bc5f5; }
                .rw-filter-select { padding: 7px 10px; border: 1.5px solid #e4e8ef; border-radius: 8px; font-size: 0.8rem; color: #4a5568; background: #fff; outline: none; cursor: pointer; transition: border-color 0.2s; }
                .rw-filter-select:focus { border-color: #5bc5f5; }
                .rw-filter-label { font-size: 0.7rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.07em; color: #8a9ab0; white-space: nowrap; }
                .rw-filter-clear { height: 33px; padding: 0 12px; border-radius: 8px; border: 1.5px solid #e4e8ef; background: #fff; color: #c62828; font-size: 0.78rem; font-weight: 700; cursor: pointer; display: inline-flex; align-items: center; gap: 5px; transition: background 0.15s; }
                .rw-filter-clear:hover { background: #fce4ec; }
                .rw-filter-results { margin-left: auto; font-size: 0.75rem; color: #8a9ab0; white-space: nowrap; }

                /* Table */
                .rw-table-wrap { overflow-x: auto; }
                table.rw-table { width: 100%; border-collapse: collapse; }
                table.rw-table thead tr { background: #f4f6f9; }
                table.rw-table thead th { padding: 10px 14px; font-size: 0.67rem; font-weight: 700; letter-spacing: 0.08em; text-transform: uppercase; color: #8a9ab0; border-bottom: 2px solid #e9ecef; white-space: nowrap; border-top: none; }
                table.rw-table thead th:first-child { padding-left: 20px; }
                table.rw-table tbody tr { border-left: 3px solid transparent; transition: background 0.12s, border-color 0.12s; }
                table.rw-table tbody tr:hover { background: #f7fbff; border-left-color: #5bc5f5; }
                table.rw-table tbody td { padding: 12px 14px; font-size: 0.82rem; color: #2d3748; border-bottom: 1px solid #f0f2f5; vertical-align: middle; }
                table.rw-table tbody td:first-child { padding-left: 20px; }
                table.rw-table tbody tr:last-child td { border-bottom: none; }

                /* Logo */
                .rw-logo { width: 56px; height: 56px; border-radius: 10px; object-fit: cover; border: 2px solid #e9ecef; }

                /* Name cell */
                .rw-name { font-weight: 700; color: #1a2a3a; font-size: 0.88rem; }
                .rw-addr { font-size: 0.72rem; color: #9aacbf; margin-top: 2px; display: flex; align-items: center; gap: 4px; }

                /* ID pill */
                .rw-id-pill { font-family: "SFMono-Regular", Consolas, monospace; font-size: 0.7rem; background: #f4f6f9; color: #6b7c93; padding: 2px 8px; border-radius: 6px; border: 1px solid #e4e8ef; }

                /* Badges */
                .rw-badge { display: inline-flex; align-items: center; gap: 5px; padding: 3px 10px; border-radius: 20px; font-size: 0.7rem; font-weight: 700; white-space: nowrap; }

                /* Products count pill (live/total) — click opens catalog QC */
                .rw-products-pill { display: inline-flex; align-items: center; gap: 6px; padding: 6px 12px; border-radius: 20px; border: 1.5px solid #dbe4f3; background: #f4f7fd; cursor: pointer; transition: all 0.15s; }
                .rw-products-pill:hover { background: #e8effc; border-color: #4169E1; transform: translateY(-1px); }
                .rw-products-live { font-size: 0.95rem; font-weight: 800; color: #1a2a3a; }
                .rw-products-total { font-size: 0.72rem; font-weight: 700; color: #8a9ab0; }

                /* Catalog QC modal product rows */
                .rw-qc-row { display: flex; align-items: center; gap: 12px; padding: 10px 12px; border: 1px solid #eef1f6; border-radius: 10px; margin-bottom: 8px; background: #fff; }
                .rw-qc-thumb { width: 46px; height: 46px; border-radius: 8px; object-fit: contain; background: #f4f6f9; border: 1px solid #e9ecef; flex-shrink: 0; }
                .rw-qc-name { font-size: 0.83rem; font-weight: 700; color: #1a2a3a; line-height: 1.3; }
                .rw-qc-meta { font-size: 0.7rem; color: #8a9ab0; margin-top: 2px; }
                .rw-qc-section-title { font-size: 0.68rem; font-weight: 800; letter-spacing: 0.07em; text-transform: uppercase; color: #8a9ab0; margin: 14px 2px 8px; }

                /* Actions */
                .rw-actions { display: flex; flex-direction: column; gap: 5px; min-width: 110px; }
                .rw-btn { height: 30px; padding: 0 12px; border-radius: 7px; border: none; display: inline-flex; align-items: center; justify-content: center; gap: 5px; font-size: 0.75rem; font-weight: 700; cursor: pointer; transition: opacity 0.15s; white-space: nowrap; width: 100%; }
                .rw-btn:disabled { opacity: 0.45; cursor: not-allowed; }
                .rw-btn-edit { background: #4169E1; color: #fff; }
                .rw-btn-edit:hover:not(:disabled) { background: #3457c9; }
                .rw-btn-del  { background: transparent; border: 1.5px solid #dc3545; color: #dc3545; }
                .rw-btn-del:hover:not(:disabled) { background: #f8d7da; }
                .rw-btn-hours { background: #117a44; color: #fff; }
                .rw-btn-hours:hover:not(:disabled) { background: #0d6135; }
                .rw-btn-creds { background: #023957; color: #fff; }
                .rw-btn-asvendor { background: transparent; border: 1.5px solid #4B0082; color: #4B0082; }
                .rw-btn-asvendor:hover { background: #4B0082; color: #fff; }
                .rw-btn-creds:hover:not(:disabled) { background: #012b42; }

                /* Mobile cards */
                .rw-card-list { display: none; padding: 12px; }
                .rw-mobile-card { background: #fff; border-radius: 10px; padding: 14px 16px; border: 1px solid #e9ecef; border-left: 4px solid #4169E1; margin-bottom: 12px; }
                .rw-mc-header { display: flex; align-items: center; gap: 12px; margin-bottom: 10px; }
                .rw-mc-logo { width: 44px; height: 44px; border-radius: 8px; object-fit: cover; border: 1px solid #e9ecef; flex-shrink: 0; }
                .rw-mc-name { font-weight: 700; color: #1a2a3a; font-size: 0.9rem; }
                .rw-mc-addr { font-size: 0.73rem; color: #9aacbf; margin-top: 2px; }
                .rw-mc-badges { display: flex; gap: 6px; flex-wrap: wrap; margin: 8px 0; }
                .rw-mc-actions { display: grid; grid-template-columns: 1fr 1fr; gap: 5px; margin-top: 10px; }

                /* Empty */
                .rw-empty { text-align: center; padding: 52px 20px; }
                .rw-empty-icon { font-size: 36px; margin-bottom: 12px; opacity: 0.2; color: #8a9ab0; display: flex; justify-content: center; }
                .rw-empty-msg { font-size: 0.88rem; color: #9aacbf; font-style: italic; }

                /* Error */
                .rw-error { display: flex; align-items: center; gap: 12px; background: #fff3f4; border: 1.5px solid #f5c2c7; border-radius: 10px; padding: 16px 20px; color: #c62828; font-size: 0.9rem; font-weight: 600; margin: 20px; }

                /* Modal */
                .rw-modal-footer-btn { height: 36px; padding: 0 20px; border-radius: 8px; border: none; font-size: 0.82rem; font-weight: 700; cursor: pointer; display: inline-flex; align-items: center; gap: 7px; }

                /* Responsive */
                @media (max-width: 900px) {
                    .rw-kpi-grid { grid-template-columns: repeat(2, 1fr); gap: 10px; }
                    .rw-kpi-icon { display: none; }
                }
                @media (max-width: 768px) {
                    .rw-page { padding: 16px 12px; }
                    .rw-table-wrap { display: none; }
                    .rw-card-list { display: block; }
                    .rw-kpi-grid { grid-template-columns: repeat(2, 1fr); }
                    .rw-filter-bar { gap: 8px; padding: 10px 12px; }
                    .rw-filter-search { max-width: 100%; }
                    .rw-filter-results { display: none; }
                }
                @media (max-width: 480px) { .rw-kpi-value { font-size: 1.5rem; } }
            `}</style>

            {loading && (
                <Spinner animation="grow" variant="primary"
                    style={{ position: 'fixed', top: '50%', left: '50%', zIndex: 9999 }}
                />
            )}

            <div className="rw-page">

                {/* ── KPI Cards ── */}
                <div className="rw-kpi-grid">
                    <div className="rw-kpi-card" style={{ borderTopColor: '#4169E1' }}>
                        <div className="rw-kpi-icon"><FaShoppingBag /></div>
                        <div className="rw-kpi-label">Total</div>
                        <div className="rw-kpi-value" style={{ color: '#4169E1' }}>{total}</div>
                        <div className="rw-kpi-sub">Registered businesses</div>
                    </div>
                    <div className="rw-kpi-card" style={{ borderTopColor: '#117a44' }}>
                        <div className="rw-kpi-icon"><FaToggleOn /></div>
                        <div className="rw-kpi-label">Active</div>
                        <div className="rw-kpi-value" style={{ color: '#117a44' }}>{active}</div>
                        <div className="rw-kpi-sub">Currently serving customers</div>
                    </div>
                    <div className="rw-kpi-card" style={{ borderTopColor: '#dc3545' }}>
                        <div className="rw-kpi-icon"><FaToggleOn style={{ transform: 'scaleX(-1)' }} /></div>
                        <div className="rw-kpi-label">Inactive</div>
                        <div className="rw-kpi-value" style={{ color: '#dc3545' }}>{inactive}</div>
                        <div className="rw-kpi-sub">Not currently serving</div>
                    </div>
                    <div className="rw-kpi-card" style={{ borderTopColor: '#f5a623' }}>
                        <div className="rw-kpi-icon"><FaStar /></div>
                        <div className="rw-kpi-label">Promoted</div>
                        <div className="rw-kpi-value" style={{ color: '#f5a623' }}>{promoted}</div>
                        <div className="rw-kpi-sub">Featured listings</div>
                    </div>
                </div>

                {/* ── Main Panel ── */}
                <div className="rw-panel">

                    {/* Dark header */}
                    <div className="rw-header">
                        <div>
                            <h4 className="rw-header-title">
                                <FaShoppingBag style={{ color: '#5bc5f5' }} />
                                Retailers &amp; Wholesalers
                            </h4>
                            <p className="rw-header-sub">Manage all registered retail and wholesale businesses</p>
                        </div>
                    </div>

                    {/* Filter bar */}
                    {!editingRetailerWholesaler && (
                        <div className="rw-filter-bar">
                            <div className="rw-filter-search">
                                <span className="rw-filter-search-icon"><FaSearch /></span>
                                <input
                                    type="text"
                                    placeholder="Search businesses…"
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                />
                            </div>

                            <span className="rw-filter-label">Status</span>
                            <AppSelect className="rw-filter-select" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                                <option value="all">All</option>
                                <option value="active">Active</option>
                                <option value="inactive">Inactive</option>
                            </AppSelect>

                            <span className="rw-filter-label">Promoted</span>
                            <AppSelect className="rw-filter-select" value={promotedFilter} onChange={(e) => setPromotedFilter(e.target.value)}>
                                <option value="all">All</option>
                                <option value="promoted">Promoted</option>
                                <option value="not-promoted">Not Promoted</option>
                            </AppSelect>

                            {hasActiveFilters && (
                                <button className="rw-filter-clear" onClick={clearFilters}>
                                    <FaFilter style={{ fontSize: 10 }} /> Clear
                                </button>
                            )}
                            <span className="rw-filter-results">{filtered.length} of {total}</span>
                        </div>
                    )}

                    {/* Edit form inline */}
                    {editingRetailerWholesaler && (
                        <div style={{ padding: '20px' }}>
                            <EditRetailerWholesalerForm
                                retailerWholesaler={editingRetailerWholesaler}
                                canEditName={isSuperAdmin}
                                onSave={handleSave}
                                onCancel={handleCancel}
                            />
                        </div>
                    )}

                    {/* Error state */}
                    {!editingRetailerWholesaler && error && (
                        <div className="rw-error">
                            <FaExclamationCircle style={{ fontSize: 20 }} />
                            {error}
                            <button onClick={handleRetry}
                                style={{ marginLeft: 'auto', background: '#c62828', color: '#fff', border: 'none', borderRadius: 7, padding: '6px 16px', fontWeight: 700, fontSize: '0.8rem', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                                <FaRedo style={{ fontSize: 10 }} /> Retry
                            </button>
                        </div>
                    )}

                    {/* Desktop table */}
                    {!editingRetailerWholesaler && !error && (
                        <div className="rw-table-wrap">
                            <table className="rw-table">
                                <thead>
                                    <tr>
                                        <th>#</th>
                                        <th>Business</th>
                                        <th>Logo</th>
                                        <th>Products</th>
                                        <th>Status</th>
                                        <th>Promoted</th>
                                        <th>AI</th>
                                        <th>ID</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {!filtered.length ? (
                                        <tr>
                                            <td colSpan={9}>
                                                <div className="rw-empty">
                                                    <div className="rw-empty-icon"><FaShoppingBag /></div>
                                                    <div className="rw-empty-msg">
                                                        {hasActiveFilters ? 'No businesses match the current filters.' : 'No businesses found.'}
                                                    </div>
                                                </div>
                                            </td>
                                        </tr>
                                    ) : (
                                        filtered.map((rw, index) => (
                                            <tr key={rw._id}>
                                                <td style={{ color: '#9aacbf', fontWeight: 700, fontSize: '0.75rem' }}>{index + 1}</td>
                                                <td>
                                                    <div className="rw-name">{rw.name}</div>
                                                    <div className="rw-addr">
                                                        <FaMapMarkerAlt style={{ fontSize: 10, color: '#5bc5f5' }} />
                                                        {rw.address || '—'}
                                                    </div>
                                                </td>
                                                <td>
                                                    <img
                                                        src={rw.imageUrl || retailerWholesalerPlaceholderImage}
                                                        alt={rw.name}
                                                        className="rw-logo"
                                                        onError={(e) => (e.target.src = retailerWholesalerPlaceholderImage)}
                                                    />
                                                </td>
                                                <td>
                                                    {(() => {
                                                        const stats = productStats(rw);
                                                        return (
                                                            <button
                                                                className="rw-products-pill"
                                                                onClick={() => setQcVendor(rw)}
                                                                title="View products (catalog quality control)"
                                                            >
                                                                <FaBoxOpen style={{ fontSize: 12, color: '#4169E1' }} />
                                                                <span className="rw-products-live">{stats.live}</span>
                                                                <span className="rw-products-total">/ {stats.total}</span>
                                                            </button>
                                                        );
                                                    })()}
                                                </td>
                                                <td>
                                                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 5 }}>
                                                        <Switch
                                                            checked={rw.isActive}
                                                            disabled
                                                            classes={{ switchBase: classes.switchBase, checked: classes.checked, track: classes.track }}
                                                        />
                                                        <span className="rw-badge" style={rw.isActive
                                                            ? { background: '#e8f5e9', color: '#2e7d32', border: '1px solid #c8e6c9' }
                                                            : { background: '#fce4ec', color: '#c62828', border: '1px solid #f5c6cb' }}>
                                                            <FaCircle style={{ fontSize: 6 }} />
                                                            {rw.isActive ? 'Active' : 'Inactive'}
                                                        </span>
                                                    </div>
                                                </td>
                                                <td>
                                                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 5 }}>
                                                        <Switch
                                                            checked={rw.promoted}
                                                            disabled
                                                            classes={{ switchBase: classes.promotedSwitchBase, checked: classes.checked, track: classes.track }}
                                                        />
                                                        <span className="rw-badge" style={rw.promoted
                                                            ? { background: '#fff8e1', color: '#f57f17', border: '1px solid #ffe082' }
                                                            : { background: '#f4f6f9', color: '#8a9ab0', border: '1px solid #e4e8ef' }}>
                                                            <FaStar style={{ fontSize: 8 }} />
                                                            {rw.promoted ? 'Promoted' : 'Standard'}
                                                        </span>
                                                    </div>
                                                </td>
                                                <td style={{ minWidth: 90 }}>
                                                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                                                        <Switch
                                                            checked={!!rw.isAIShoppable}
                                                            disabled={!canManageRetailWholesale || !!aiToggleLoading[rw._id]}
                                                            onChange={() => handleToggleAIShoppable(rw._id, rw.name, rw.isAIShoppable)}
                                                            size="small"
                                                            classes={{ switchBase: classes.switchBase, checked: classes.checked, track: classes.track }}
                                                        />
                                                        <span style={{
                                                            display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: '0.65rem',
                                                            fontWeight: 700, padding: '2px 7px', borderRadius: 20,
                                                            ...(rw.isAIShoppable
                                                                ? { background: '#e8f5e9', color: '#2e7d32', border: '1px solid #c8e6c9' }
                                                                : { background: '#f4f6f9', color: '#9aacbf', border: '1px solid #e4e8ef' })
                                                        }}>
                                                            {rw.isAIShoppable ? '🤖 AI On' : 'AI Off'}
                                                        </span>
                                                    </div>
                                                </td>
                                                <td>
                                                    <span className="rw-id-pill" title={rw._id}>…{rw._id?.slice(-8)}</span>
                                                </td>
                                                <td>
                                                    <div className="rw-actions">
                                                        <button className="rw-btn rw-btn-edit" onClick={() => handleEditClick(rw)} disabled={!canUpdateRetailWholesale}>
                                                            <FaEdit style={{ fontSize: 10 }} /> Edit
                                                        </button>
                                                        <button className="rw-btn rw-btn-del" onClick={() => handleDeleteRetailerWholesaler(rw.owner)} disabled={!canDeleteRetailWholesale}>
                                                            <FaTrash style={{ fontSize: 10 }} /> Delete
                                                        </button>
                                                        <button className="rw-btn rw-btn-hours" onClick={() => handleManageHours(rw)} disabled={!canUpdateRetailWholesale}>
                                                            <FaClock style={{ fontSize: 10 }} /> Manage Hours
                                                        </button>
                                                        {/* Super-admin only: mail the vendor their login
                                                            email + a single-use set-password link. */}
                                                        <SendVendorCredentialsAction
                                                            vendorType="RETAILER_WHOLESALER"
                                                            vendorId={rw._id}
                                                            vendorName={rw.name}
                                                            visible={isSuperAdmin}
                                                        >
                                                            {({ open, busy }) => (
                                                                <button className="rw-btn rw-btn-creds" onClick={open} disabled={busy}>
                                                                    <FaKey style={{ fontSize: 10 }} /> {busy ? 'Sending…' : 'Send Login'}
                                                                </button>
                                                            )}
                                                        </SendVendorCredentialsAction>
                                                        <SignInAsVendorButton
                                                            ownerId={rw.owner?._id || rw.owner}
                                                            vendorId={rw._id}
                                                            vendorName={rw.name}
                                                            className="rw-btn rw-btn-asvendor"
                                                        />
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {/* Mobile card list */}
                    {!editingRetailerWholesaler && !error && (
                        <div className="rw-card-list">
                            {!filtered.length ? (
                                <div className="rw-empty">
                                    <div className="rw-empty-icon"><FaShoppingBag /></div>
                                    <div className="rw-empty-msg">
                                        {hasActiveFilters ? 'No businesses match the current filters.' : 'No businesses found.'}
                                    </div>
                                </div>
                            ) : (
                                filtered.map((rw) => (
                                    <div key={rw._id} className="rw-mobile-card">
                                        <div className="rw-mc-header">
                                            <img
                                                src={rw.imageUrl || retailerWholesalerPlaceholderImage}
                                                alt={rw.name}
                                                className="rw-mc-logo"
                                                onError={(e) => (e.target.src = retailerWholesalerPlaceholderImage)}
                                            />
                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                <div className="rw-mc-name">{rw.name}</div>
                                                <div className="rw-mc-addr">{rw.address || '—'}</div>
                                            </div>
                                        </div>
                                        <div className="rw-mc-badges">
                                            <span className="rw-badge" style={rw.isActive
                                                ? { background: '#e8f5e9', color: '#2e7d32', border: '1px solid #c8e6c9' }
                                                : { background: '#fce4ec', color: '#c62828', border: '1px solid #f5c6cb' }}>
                                                <FaCircle style={{ fontSize: 6 }} />
                                                {rw.isActive ? 'Active' : 'Inactive'}
                                            </span>
                                            <span className="rw-badge" style={rw.promoted
                                                ? { background: '#fff8e1', color: '#f57f17', border: '1px solid #ffe082' }
                                                : { background: '#f4f6f9', color: '#8a9ab0', border: '1px solid #e4e8ef' }}>
                                                <FaStar style={{ fontSize: 8 }} />
                                                {rw.promoted ? 'Promoted' : 'Standard'}
                                            </span>
                                            <span className="rw-badge" style={rw.isAIShoppable
                                                ? { background: '#e8f5e9', color: '#2e7d32', border: '1px solid #c8e6c9' }
                                                : { background: '#f4f6f9', color: '#9aacbf', border: '1px solid #e4e8ef' }}>
                                                {rw.isAIShoppable ? '🤖 AI On' : 'AI Off'}
                                            </span>
                                            {(() => {
                                                const stats = productStats(rw);
                                                return (
                                                    <span
                                                        className="rw-badge"
                                                        onClick={() => setQcVendor(rw)}
                                                        style={{ background: '#f4f7fd', color: '#4169E1', border: '1px solid #dbe4f3', cursor: 'pointer' }}
                                                    >
                                                        <FaBoxOpen style={{ fontSize: 9 }} />
                                                        {stats.live}/{stats.total} products
                                                    </span>
                                                );
                                            })()}
                                        </div>
                                        <div className="rw-mc-actions">
                                            <button className="rw-btn rw-btn-edit" onClick={() => handleEditClick(rw)} disabled={!canUpdateRetailWholesale}>
                                                <FaEdit style={{ fontSize: 10 }} /> Edit
                                            </button>
                                            <button className="rw-btn rw-btn-del" onClick={() => handleDeleteRetailerWholesaler(rw.owner)} disabled={!canDeleteRetailWholesale}>
                                                <FaTrash style={{ fontSize: 10 }} /> Delete
                                            </button>
                                            <button className="rw-btn rw-btn-hours" onClick={() => handleManageHours(rw)} disabled={!canUpdateRetailWholesale}>
                                                <FaClock style={{ fontSize: 10 }} /> Manage Hours
                                            </button>
                                            <SendVendorCredentialsAction
                                                vendorType="RETAILER_WHOLESALER"
                                                vendorId={rw._id}
                                                vendorName={rw.name}
                                                visible={isSuperAdmin}
                                            >
                                                {({ open, busy }) => (
                                                    <button className="rw-btn rw-btn-creds" onClick={open} disabled={busy}>
                                                        <FaKey style={{ fontSize: 10 }} /> {busy ? 'Sending…' : 'Send Login'}
                                                    </button>
                                                )}
                                            </SendVendorCredentialsAction>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    )}

                </div>
            </div>

            {/* ── AI Commerce Toggle Confirmation Modal ── */}
            <Modal
                show={showAIModal}
                onHide={() => { setShowAIModal(false); setAiModalTarget(null); }}
                centered
                size="sm"
            >
                <Modal.Header
                    closeButton
                    style={{
                        borderBottom: `2px solid ${aiModalTarget?.currentValue ? '#dc3545' : '#117a44'}`,
                        background: '#1a2a3a',
                    }}
                >
                    <Modal.Title>
                        {aiModalTarget?.currentValue ? '🤖 Disable AI Commerce' : '🤖 Enable AI Commerce'}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ padding: '20px 24px' }}>
                    <p style={{ fontSize: '0.88rem', marginBottom: 10 }}>
                        {aiModalTarget?.currentValue
                            ? <>AI agents will <strong>no longer</strong> be able to access <strong>{aiModalTarget?.name}</strong>.</>
                            : <><strong>{aiModalTarget?.name}</strong> will become discoverable and orderable by AI agents (ChatGPT, Gemini, etc.).</>}
                    </p>
                    <div style={{
                        background: aiModalTarget?.currentValue ? '#fff3cd' : '#e8f5e9',
                        border: `1px solid ${aiModalTarget?.currentValue ? '#ffc107' : '#c8e6c9'}`,
                        borderRadius: 8, padding: '10px 14px', fontSize: '0.78rem',
                        color: aiModalTarget?.currentValue ? '#856404' : '#2e7d32',
                    }}>
                        {aiModalTarget?.currentValue
                            ? '⚠️ This will immediately remove the vendor from the AI Commerce Gateway.'
                            : '✅ This will immediately expose the vendor to the AI Commerce Gateway.'}
                    </div>
                </Modal.Body>
                <Modal.Footer style={{ borderTop: '1px solid #f0f2f5', gap: 8 }}>
                    <button
                        className="rw-modal-footer-btn"
                        onClick={() => { setShowAIModal(false); setAiModalTarget(null); }}
                        style={{ background: '#f4f6f9', color: '#6b7c93', border: '1.5px solid #e4e8ef' }}
                    >
                        Cancel
                    </button>
                    <button
                        className="rw-modal-footer-btn"
                        onClick={confirmToggleAI}
                        style={{ background: aiModalTarget?.currentValue ? '#dc3545' : '#117a44', color: '#fff' }}
                    >
                        {aiModalTarget?.currentValue ? 'Disable AI' : 'Enable AI'}
                    </button>
                </Modal.Footer>
            </Modal>

            {/* ── Catalog Quality-Control Modal ──
                Lists the vendor's live/hidden products; super admins can jump
                into any listing and polish it (name, rich description, images)
                via the standard product editor in admin QC mode. */}
            <Modal show={Boolean(qcVendor)} onHide={() => setQcVendor(null)} centered size="lg" scrollable>
                <Modal.Header closeButton style={{ background: '#fafbfc', borderBottom: '2px solid #4169E1' }}>
                    <Modal.Title style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <FaBoxOpen style={{ color: '#5bc5f5' }} />
                        {qcVendor?.name} — Product Listings
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ padding: '16px 20px', background: '#f8fafc', maxHeight: '65vh' }}>
                    {(qcVendor?.sections || []).filter((s) => s?.products?.length).length === 0 ? (
                        <div className="rw-empty">
                            <div className="rw-empty-icon"><FaBoxOpen /></div>
                            <div className="rw-empty-msg">This vendor has no product listings yet.</div>
                        </div>
                    ) : (
                        (qcVendor?.sections || [])
                            .filter((s) => s?.products?.length)
                            .map((section) => (
                                <div key={section._id}>
                                    <div className="rw-qc-section-title">{section.title}</div>
                                    {section.products.map((p) => (
                                        <div key={p._id} className="rw-qc-row">
                                            <img
                                                src={p.image || retailerWholesalerPlaceholderImage}
                                                alt={p.name}
                                                className="rw-qc-thumb"
                                                onError={(e) => (e.target.src = retailerWholesalerPlaceholderImage)}
                                            />
                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                <div className="rw-qc-name">{p.name}</div>
                                                <div className="rw-qc-meta">
                                                    {Number(p.price).toLocaleString()} · stock {p.countInStock ?? '—'}
                                                    {!p.descriptionHtml && p.description ? ' · plain-text description' : ''}
                                                    {!p.description && !p.descriptionHtml ? ' · no description' : ''}
                                                </div>
                                            </div>
                                            <span className="rw-badge" style={p.enabled
                                                ? { background: '#e8f5e9', color: '#2e7d32', border: '1px solid #c8e6c9' }
                                                : { background: '#fce4ec', color: '#c62828', border: '1px solid #f5c6cb' }}>
                                                <FaCircle style={{ fontSize: 6 }} />
                                                {p.enabled ? 'Live' : 'Hidden'}
                                            </span>
                                            {canUpdateRetailWholesale && (
                                                <button
                                                    className="rw-btn rw-btn-edit"
                                                    style={{ width: 'auto' }}
                                                    onClick={() => handleQcEditProduct(p, section.title)}
                                                >
                                                    <FaEdit style={{ fontSize: 10 }} /> Improve
                                                </button>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            ))
                    )}
                </Modal.Body>
                <Modal.Footer style={{ borderTop: '1px solid #f0f2f5' }}>
                    {!canUpdateRetailWholesale && (
                        <span style={{ marginRight: 'auto', fontSize: '0.75rem', color: '#8a9ab0' }}>
                            You have view-only access to vendor listings.
                        </span>
                    )}
                    <button className="rw-modal-footer-btn" onClick={() => setQcVendor(null)}
                        style={{ background: '#f4f6f9', color: '#6b7c93', border: '1.5px solid #e4e8ef' }}>
                        Close
                    </button>
                </Modal.Footer>
            </Modal>

            {/* ── Delete Modal ── */}
            <Modal show={showDeleteModal} onHide={toggleDeleteModal} centered>
                <Modal.Header closeButton style={{ borderBottom: '2px solid #dc3545', background: '#fafbfc' }}>
                    <Modal.Title style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <FaTrash style={{ color: '#dc3545' }} />
                        Confirm Deletion
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ padding: '20px 24px' }}>
                    <div style={{ textAlign: 'center' }}>
                        <FaExclamationTriangle style={{ fontSize: '2.5rem', color: '#dc3545', marginBottom: 12 }} />
                        <p style={{ fontSize: '0.9rem', marginBottom: 8 }}>
                            Are you sure you want to delete this retailer / wholesaler?
                        </p>
                        <p style={{ fontSize: '0.78rem', color: '#9aacbf' }}>This action cannot be undone.</p>
                    </div>
                </Modal.Body>
                <Modal.Footer style={{ borderTop: '1px solid #f0f2f5' }}>
                    <button className="rw-modal-footer-btn" onClick={toggleDeleteModal}
                        style={{ background: '#f4f6f9', color: '#6b7c93', border: '1.5px solid #e4e8ef' }}>
                        Cancel
                    </button>
                    <button className="rw-modal-footer-btn" onClick={confirmDeleteRetailerWholesaler}
                        style={{ background: '#dc3545', color: '#fff' }}>
                        <FaTrash style={{ fontSize: 11 }} /> Delete
                    </button>
                </Modal.Footer>
            </Modal>

            {/* ── Manage Business Hours Modal ── */}
            <Modal show={showHoursModal} onHide={() => setShowHoursModal(false)} size="lg" centered>
                <Modal.Header closeButton>
                    <Modal.Title style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <FaClock style={{ color: '#083860' }} />
                        Manage Business Hours: {vendorForHours?.name}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ padding: '20px 24px' }}>
                    {vendorForHours && (
                        <div>
                            <div style={{ background: '#e8f5e9', border: '1.5px solid #c8e6c9', borderRadius: 8, padding: '12px 16px', marginBottom: 16, fontSize: '0.85rem' }}>
                                <strong style={{ color: '#2e7d32' }}>Shop Operating Hours</strong>
                                <p style={{ margin: '6px 0 0', color: '#4a5568' }}>
                                    Set when <strong>{vendorForHours.name}</strong> is open for orders. Shoppers see a
                                    closed notice outside these hours and cannot add to cart.
                                </p>
                            </div>
                            {isLoadingHours ? (
                                <div style={{ textAlign: 'center', padding: '40px 0' }}>
                                    <Spinner animation="border" variant="success" />
                                    <p style={{ marginTop: 10, color: '#9aacbf', fontSize: '0.85rem' }}>Loading business hours…</p>
                                </div>
                            ) : (
                                <OpenHoursManager
                                    openHours={vendorHours}
                                    // OpenHoursManager renders "Set your {vendorLabel}'s
                                    // operating hours" — it appends the possessive itself,
                                    // so this must be a bare noun. Matches
                                    // RetailerWholesalerProfile.js:881.
                                    vendorLabel="shop"
                                    // Shops trade 24/7 until hours are saved.
                                    tradingPolicy="open_until_set"
                                    vendorType="retailer"
                                    vendorId={vendorForHours?._id}
                                    timezone={vendorForHours?.timezone || 'Africa/Kampala'}
                                    onSave={handleSaveVendorHours}
                                    isLoading={isSavingHours}
                                    onValidationError={() => toast.error('Please fix validation errors before saving')}
                                />
                            )}
                        </div>
                    )}
                </Modal.Body>
            </Modal>
        </>
    );
};

export default RetailersWholesalers;
